/**
 * Derived from the pathname (not `useParams`) because the Assistant panel mounts
 * outside the nested route tree. Identity-only: never carries on-screen data.
 */
import * as React from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import type { PageContext } from 'shared/src/api/page-context-types';

type RouteParams = Record<string, string | undefined>;

type DerivedContext = Omit<PageContext, 'route'>;

type Rule = {
  /** `/*` suffix matches a screen plus its sub-tabs. */
  pattern: string;
  build: (params: RouteParams) => DerivedContext;
};

/** Campaign Number (CN-{id}) is the label users actually say. */
function campaignLabel(campaignId: string | undefined): string {
  return campaignId ? `CN-${campaignId}` : 'Campaign';
}

// Most-specific first: the first match wins, so the media-buy (media plan) detail
// must precede the line-item detail that wraps it, and detail routes precede lists.
const RULES: Rule[] = [
  {
    pattern: '/campaigns/:campaignId/execution/lineitems/:lineItemId/mediaplans/:mediaPlanId',
    build: p => ({
      entity: 'mediaBuy',
      id: p.mediaPlanId ?? null,
      label: 'Media buy',
      view: 'execution'
    })
  },
  {
    pattern: '/campaigns/:campaignId/execution/lineitems/:lineItemId/*',
    build: p => ({
      entity: 'lineItem',
      id: p.lineItemId ?? null,
      label: 'Line item',
      view: 'execution'
    })
  },
  {
    pattern: '/campaigns/:campaignId/proposal/lineitems/:lineItemId/*',
    build: p => ({
      entity: 'lineItem',
      id: p.lineItemId ?? null,
      label: 'Line item',
      view: 'proposal'
    })
  },
  {
    pattern: '/campaigns/:campaignId/execution/*',
    build: p => ({
      entity: 'campaign',
      id: p.campaignId ?? null,
      label: campaignLabel(p.campaignId),
      view: 'execution'
    })
  },
  {
    pattern: '/campaigns/:campaignId/proposal/*',
    build: p => ({
      entity: 'campaign',
      id: p.campaignId ?? null,
      label: campaignLabel(p.campaignId),
      view: 'proposal'
    })
  },
  {
    pattern: '/campaigns/:campaignId/activity/*',
    build: p => ({
      entity: 'campaign',
      id: p.campaignId ?? null,
      label: campaignLabel(p.campaignId),
      view: 'activity'
    })
  },
  {
    pattern: '/campaigns/:campaignId/*',
    build: p => ({
      entity: 'campaign',
      id: p.campaignId ?? null,
      label: campaignLabel(p.campaignId),
      view: 'overview'
    })
  },
  {
    pattern: '/campaigns',
    build: () => ({ entity: null, id: null, label: 'Campaigns', view: 'list' })
  },
  {
    pattern: '/line-items',
    build: () => ({ entity: null, id: null, label: 'Line items', view: 'list' })
  },
  {
    pattern: '/media-buys',
    build: () => ({ entity: null, id: null, label: 'Media buys', view: 'list' })
  },
  {
    pattern: '/activity',
    build: () => ({ entity: null, id: null, label: 'Activity', view: 'activity' })
  },
  {
    pattern: '/viz',
    build: () => ({ entity: null, id: null, label: 'Visualization', view: 'viz' })
  }
];

/** Empty label so the chip is suppressed and nothing meaningless is stamped for unrecognized screens. */
const UNKNOWN: DerivedContext = { entity: null, id: null, label: '', view: null };

export function derivePageContext(pathname: string): PageContext {
  for (const rule of RULES) {
    const match = matchPath({ path: rule.pattern, end: true }, pathname);
    if (match) return { route: pathname, ...rule.build(match.params) };
  }
  return { route: pathname, ...UNKNOWN };
}

export function useCurrentPageContext(): PageContext {
  const { pathname } = useLocation();
  return React.useMemo(() => derivePageContext(pathname), [pathname]);
}

export function hasPageContext(context: PageContext): boolean {
  return context.entity !== null || context.view !== null;
}
